import useResources from "./use-resources";
import useAuthenticatedAxios from "./use-authenticated-axios";
import { useMemo } from "react";
import useSWR, { useSWRConfig } from "swr";
import useSWRImmutable from "swr/immutable";
import { BalanceDomain, DOMAIN_TO_ROOT_URL } from "../utils/constants/balances";
import moment from "moment/moment";
import useResource from "./use-resource";
import { useToast } from "../components/UI/Toast/useToast";
import { findAndUpdate } from "../utils";

function getRootUrl(domain, parentId) {
  return parentId ? `${DOMAIN_TO_ROOT_URL[domain]}/${parentId}/balances` : null;
}

export function useTenantBalances(tenantId, isLocataire) {
  const rootUrl = getRootUrl(BalanceDomain.TENANT, tenantId);
  const { mutate, axios, refreshBalanceSums, ...rest } = useBalances(
    BalanceDomain.TENANT,
    tenantId,
    {
      params: { is_locataire: isLocataire },
    },
  );

  async function regenerateBalances(balances) {
    const result = await mutate(
      async () => {
        const res = await axios.post(`${rootUrl}/regenerate`, balances);
        return res.data;
      },
      { revalidate: false },
    );
    await refreshBalanceSums();
    return result;
  }

  async function setUpToDateBalances() {
    const result = await mutate(
      async () => {
        const res = await axios.post(`${rootUrl}/up-to-date`);
        return res.data;
      },
      { revalidate: false },
    );
    await refreshBalanceSums();
    return result;
  }

  async function applyAuditActions(actions) {
    const result = await mutate(
      async () => {
        const res = await axios.post(`${rootUrl}/audit/apply`, { actions });
        return res.data;
      },
      { revalidate: false },
    );
    await refreshBalanceSums();
    return result;
  }

  return {
    regenerateBalances,
    setUpToDateBalances,
    applyAuditActions,
    ...rest,
  };
}

export function useAllTenantsBalances(isLocataire, options = {}) {
  const { groupBy } = options;
  const {
    resources: balances,
    groupedResources: groupedBalances,
    mutate,
  } = useResources(
    "balance_tenants/balance",
    {
      params: { is_locataire: isLocataire },
    },
    { groupBy },
  );
  return {
    balances,
    groupedBalances,
    mutate,
  };
}

export function useTenantAccountBalance(tenantId, isLocataire) {
  const {
    resource: balances,
    mutate,
    isLoading,
  } = useResource(tenantId ? `balance_tenants/${tenantId}/balance` : null, {
    params: { is_locataire: isLocataire },
  });
  return {
    balances,
    mutate,
    isLoading,
  };
}

export function useTenantsBalanceQuittance(monthsFromNow) {
  const axios = useAuthenticatedAxios();
  const fallbackData = useMemo(() => {
    return {};
  }, []);
  const { data, error, isLoading } = useSWR(
    ["balance_tenants/quittance", monthsFromNow],
    ([url, monthsFromNow]) => {
      return axios
        .get(url, { params: { months_from_now: monthsFromNow } })
        .then((res) => res.data);
    },
    { fallbackData },
  );

  return {
    balanceQuittance: data.balances_quittances || fallbackData,
    monthsFromNow: data.months_from_now || 0,
    error,
    isLoading,
  };
}

export default function useBalances(domain, parentId, options = {}) {
  const {
    deleteBalance: rawDeleteBalance,
    deleteGroupedBalance,
    updateBalance: rawUpdateBalance,
    createBalance: rawCreateBalance,
    duplicateBalance: rawDuplicateBalance,
    batchCreateBalances: rawBatchCreateBalances,
    ...rest
  } = useRawBalances(getRootUrl(domain, parentId), options);

  function deleteBalance(id) {
    return rawDeleteBalance(id, { alternateRootPath: "balances" });
  }

  function updateBalance(id, balance) {
    return rawUpdateBalance(id, balance, {
      sort: (a, b) => moment(b.date_transac) - moment(a.date_transac),
      alternateRootPath: "balances",
    });
  }

  function createBalance(balance, { shouldMutate = true } = {}) {
    return rawCreateBalance(balance, {
      sort: (a, b) => moment(b.date_transac) - moment(a.date_transac),
      alternateRootPath: "balances",
      shouldMutate,
    });
  }

  function batchCreateBalances(balances) {
    return rawBatchCreateBalances(balances, {
      sort: (a, b) => moment(b.date_transac) - moment(a.date_transac),
      alternateRootPath: "balances",
    });
  }

  function duplicateBalance(balanceId) {
    return rawDuplicateBalance(balanceId, {
      sort: (a, b) => moment(b.date_transac) - moment(a.date_transac),
      alternateRootPath: "balances",
    });
  }

  return {
    deleteBalance,
    deleteGroupedBalance,
    updateBalance,
    createBalance,
    duplicateBalance,
    batchCreateBalances,
    ...rest,
  };
}

export function useRawBalances(url, options = {}) {
  const {
    resources: balances,
    error,
    isLoading,
    mutate,
    axios,
    create: rawCreateBalance,
    batchCreate: rawBatchCreateBalances,
    duplicate: rawDuplicateBalance,
  } = useResources(url, options);
  const toast = useToast();
  const { mutate: globalMutate } = useSWRConfig();

  // Le solde global (en-tête du locataire, badges des listes, dashboard,
  // quittançable) vit sur d'autres clés SWR que la liste des écritures : sans
  // cette invalidation, il reste périmé après une modification.
  function refreshBalanceSums() {
    return globalMutate(
      (key) =>
        (typeof key === "object" &&
          key !== null &&
          /balance_tenants(\/\d+)?\/balance/.test(key.url ?? "")) ||
        (Array.isArray(key) && key[0] === "balance_tenants/quittance"),
    );
  }

  function withSumsRefresh(mutation) {
    return async (...args) => {
      const result = await mutation(...args);
      await refreshBalanceSums();
      return result;
    };
  }

  const createBalance = withSumsRefresh(rawCreateBalance);
  const batchCreateBalances = withSumsRefresh(rawBatchCreateBalances);
  const duplicateBalance = withSumsRefresh(rawDuplicateBalance);

  function notifyContrepassations(count) {
    if (count > 1) {
      toast.info({
        title: "Écritures contrepassées",
        description: `Ces écritures sont comptabilisées dans une période clôturée : elles n'ont pas été supprimées. ${count} contrepassations datées du jour ont été créées à la place.`,
      });
    } else {
      toast.info({
        title: "Écriture contrepassée",
        description:
          "Cette écriture est comptabilisée dans une période clôturée : elle n'a pas été supprimée. Une contrepassation datée du jour a été créée à la place.",
      });
    }
  }

  function notifyAmendement() {
    toast.info({
      title: "Écriture corrigée par contrepassation",
      description:
        "Cette écriture est comptabilisée dans une période clôturée : elle n'a pas été modifiée. Une contrepassation et une ligne corrigée datées du jour ont été créées à la place.",
    });
  }

  // Corriger une écriture comptabilisée ne la modifie pas : le back la laisse en place,
  // ajoute une contrepassation et renvoie une *nouvelle* ligne corrigée. La substituer à
  // l'originale ferait disparaître de la liste une écriture qui existe toujours, et
  // masquerait la contrepassation — d'où la relecture quand l'id renvoyé a changé.
  async function updateBalance(id, balance, { sort, alternateRootPath } = {}) {
    const response = await axios.put(
      `${alternateRootPath ?? url}/${id}`,
      balance,
    );
    const updated = response.data;
    if (updated.id !== id) {
      await mutate();
      notifyAmendement();
      return updated;
    }
    await mutate(
      (currentBalances) => {
        const newBalances = findAndUpdate(
          currentBalances || [],
          (currentBalance) => currentBalance.id === id,
          updated,
        );
        if (sort) newBalances.sort(sort);
        return newBalances;
      },
      { revalidate: false },
    );
    return updated;
  }

  // Une écriture comptabilisée dans une période clôturée n'est pas supprimable : le back
  // la laisse en place, crée une contrepassation datée du jour et la renvoie (204 quand la
  // ligne a bien été supprimée). On recharge alors la liste — l'originale y reste et la
  // contrepassation s'y ajoute — au lieu de retirer une ligne qui existe toujours.
  async function deleteBalance(id, { alternateRootPath } = {}) {
    const response = await axios.delete(`${alternateRootPath ?? url}/${id}`);
    if (response.status === 204) {
      await mutate(
        (currentBalances) =>
          (currentBalances || []).filter((balance) => balance.id !== id),
        { revalidate: false },
      );
      return null;
    }
    await mutate();
    notifyContrepassations(1);
    return response.data;
  }

  function batchUpdateBalances(balances, { requestOptions } = {}) {
    return mutate(
      async () => {
        const res = await axios.put(
          `${url}/batch-update`,
          balances,
          requestOptions,
        );
        return res.data;
      },
      { revalidate: true },
    );
  }

  // Même contrat que deleteBalance, au pluriel : 204 si tout le groupe a été supprimé,
  // sinon les contrepassations créées pour les lignes comptabilisées.
  async function deleteGroupedBalance(correlationId) {
    const response = await axios.delete(`balances/correlated/${correlationId}`);
    if (response.status === 204) {
      await mutate(
        (currentBalances) =>
          (currentBalances || []).filter(
            (balance) => balance.correlation_id !== correlationId,
          ),
        { revalidate: false },
      );
      return [];
    }
    await mutate();
    notifyContrepassations(response.data.length);
    return response.data;
  }

  return {
    balances,
    error,
    isLoading,
    deleteBalance: withSumsRefresh(deleteBalance),
    deleteGroupedBalance: withSumsRefresh(deleteGroupedBalance),
    updateBalance: withSumsRefresh(updateBalance),
    createBalance,
    batchCreateBalances,
    batchUpdateBalances: withSumsRefresh(batchUpdateBalances),
    duplicateBalance,
    mutate,
    axios,
    refreshBalances: mutate,
    refreshBalanceSums,
  };
}

export function useBalanceTypes() {
  const axios = useAuthenticatedAxios();

  const {
    data: balanceTypes,
    error,
    isLoading,
  } = useSWRImmutable("balance-types", (url) => {
    return axios.get(url).then((res) => res.data);
  });

  const balanceTypesList = balanceTypes ? Object.values(balanceTypes) : [];

  const balanceTypesByCategory = Object.fromEntries(
    [
      ...new Set(balanceTypesList.map((balanceType) => balanceType.category)),
    ].map((category) => [
      category,
      balanceTypesList.filter(
        (balanceType) => balanceType.category === category,
      ),
    ]),
  );

  return {
    balanceTypes,
    balanceTypesByCategory,
    error,
    isLoading,
  };
}

const NO_FAMILIES = [];

export function useBalanceTypeFamilies() {
  const axios = useAuthenticatedAxios();

  // Pas de `fallbackData` ici : combiné à `useSWRImmutable`, il désactive la requête au
  // montage et la liste resterait vide pour toujours.
  const { data, error, isLoading } = useSWRImmutable(
    "balance-type-families",
    (url) => axios.get(url).then((res) => res.data),
  );

  return { families: data ?? NO_FAMILIES, error, isLoading };
}

export function useBalanceChargesRecuperables(propertyId, tenantId = null) {
  const url = propertyId ? `biens/${propertyId}/charges-recuperables` : null;
  const {
    resources: balanceRecuperables,
    error,
    isLoading,
  } = useResources(url, { params: { id_tenant: tenantId } });
  return {
    balanceRecuperables: balanceRecuperables,
    error,
    isLoading,
  };
}

export function useBalanceSearch(query) {
  const axios = useAuthenticatedAxios();
  const fallbackData = useMemo(() => [], []);

  const {
    data: balances,
    error,
    isLoading,
  } = useSWR(
    ["balances/search", query],
    ([url, query]) => {
      return axios.get(url, { params: { ...query } }).then((res) => res.data);
    },
    { fallbackData },
  );

  return {
    balances,
    error,
    isLoading,
  };
}

export function useSumBalanceTenants(options = {}) {
  const { isLocataire = false } = options;
  const { resources: sumBalances } = useResources("balance_tenants/balance", {
    params: { isLocataire },
  });

  return {
    sumBalances,
  };
}

export function useBalanceRecapProperties() {
  const { resources: recapProperties } = useResources("biens/recap-compta", {});

  return {
    recapProperties,
  };
}
